/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IDictionary,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import {
    ArrayExtensions,
    generateCacheKey,
    QueryResultSettingsProxy,generateProductImageUrl
} from '@msdyn365-commerce-modules/retail-actions';

/**
 * Product Input.
 */
export class ProductInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public warehouseId?: string;

    public catalogId?: number;

    private readonly apiSettings: ICommerceApiSettings;

    public constructor(
        productId: number | string,
        apiSettings: ICommerceApiSettings,
        channelId?: number,
        warehouseId?: string,
        requestContext?: IRequestContext,
        catalogId?: number
    ) {
        this.apiSettings = apiSettings;
        this.productId = +productId;
        this.channelId = channelId || apiSettings.channelId;
        this.warehouseId = warehouseId;
        this.catalogId = catalogId;

        if (requestContext && catalogId === undefined) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'ProductRecordId', value: this.productId },
            { key: 'ChannelId', value: this.channelId },
            { key: 'WarehouseId', value: this.warehouseId },
            { key: 'CatalogId', value: this.catalogId }
        );

    public getCacheObjectType = () => 'SimpleProduct';

    public dataCacheType = (): CacheType => 'none'; //'application';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createSimpleProductsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput[] => {
    let productIds = inputData.config && inputData.config.productIds;
    if (!productIds) {
        return [];
    }

    productIds = typeof productIds === 'string' ? productIds.split(',') : productIds;
    return !Array.isArray(productIds)
        ? []
        : productIds.map((productId: string) => {
              return new ProductInput(productId, inputData.requestContext.apiSettings, undefined, undefined, inputData.requestContext);
          });
};

/**
 * Calls the Retail API and returns the product based on the passed ProductInput.
 * @param inputs
 * @param ctx
 */
export async function getSimpleProductsAction(inputs: ProductInput[], ctx: IActionContext): Promise<SimpleProduct[]> {
    if (!ArrayExtensions.hasElements(inputs)) {
        ctx.trace('[getSimpleProductsAction] Invalid or empty inputs passed.');
        return [];
    }

    const productIdMapping: IDictionary<number> = {};

    for (let index = 0; index < inputs.length; ++index) {
        productIdMapping[inputs[index].productId] = index;
    }

    const queryResultSettings = QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx);
    queryResultSettings.Paging!.Top = 1000;

    const getProductPromises: Promise<SimpleProduct[]>[] = [];
    const catalogIds = ArrayExtensions.unique(inputs.map(input => input.catalogId));
    for (const catalogId of catalogIds) {
        const productInputs = inputs.filter(input => input.catalogId === catalogId);
        getProductPromises.push(
            getByIdsAsync(
                { callerContext: ctx, queryResultSettings },
                inputs[0].channelId,
                productInputs.map(input => input.productId),
                inputs[0].warehouseId ?? null,
                catalogId ?? 0
            )
                .then(response => {
                    return response;
                })
                .catch(error => {
                    ctx.telemetry.debug(`[getSimpleProductsAction] Error in getting response from server. ${error}`);
                    return <SimpleProduct[]>{};
                })
        );
    }

    const products = ArrayExtensions.flatten(await Promise.all(getProductPromises));
    const mappedProducts = products
        .map(product => {
            try {
                const newImageUrl = generateProductImageUrl(product, ctx.requestContext.apiSettings);

                if (newImageUrl) {
                    product.PrimaryImageUrl = newImageUrl;
                }

                return product;
            } catch {
                ctx.trace('[getSimpleProductsAction] Unable to update ImageURL for Product');
                return undefined;
            }
        })
        .reduce((memo: SimpleProduct[], product: SimpleProduct | undefined) => {
            if (!product) {
                return memo;
            }
            const index = productIdMapping[product.RecordId];
            memo[index] = product;
            return memo;
        }, []);

    // NOTE: <ZAFINE> This is not an ideal fix, but will resolve all current issues
    return inputs.map(input => {
        const foundProduct = mappedProducts.find(product => product && product.RecordId === input.productId);
        return foundProduct || <SimpleProduct>{};
    });
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-simple-products',
    action: <IAction<SimpleProduct[]>>getSimpleProductsAction,
    input: createSimpleProductsInput,
    isBatched: true
});
